import { Component, Input, ViewChild, ElementRef, OnInit } from '@angular/core';
import JSZip from 'jszip';
import { BulkUploadEntityType, BulkUploadIconsResponse, BulkUploadStatus } from 'src/app/shared/models/shared.models';
import { ProductDepartmentsService } from 'src/app/shared/modules/product-departments/services/product-departments.service';
import { CustomToastService } from 'src/app/core/services/custom-toast.service';
import { MasterService } from 'src/app/core/services/master.service';

@Component({
    selector: 'app-bulk-upload-icons',
    templateUrl: './bulk-upload-icons.component.html',
    styleUrls: ['./bulk-upload-icons.component.css'],
    standalone: false
})
export class BulkUploadIconsComponent implements OnInit {
    @Input() entityType!: BulkUploadEntityType;
    @ViewChild('fileInput') fileInput!: ElementRef;

    loading: boolean = false;
    uploadResult: BulkUploadIconsResponse | null = null;
    selectedFile: File | null = null;
    BulkUploadStatus = BulkUploadStatus;
    companyId: number = 0;

    constructor(
        private departmentService: ProductDepartmentsService,
        private toastService: CustomToastService,
        private masterService: MasterService
    ) { }

    ngOnInit() {
        const userInfo = this.masterService.getUserDetailFromStorage();
        this.companyId = userInfo?.companyid || 0;
    }

    get entityTypeName(): string {
        return this.entityType === BulkUploadEntityType.Department ? 'Department' : 'Category';
    }

    triggerFileInput() {
        this.fileInput.nativeElement.click();
    }

    async onFileSelected(event: any) {
        const file = event.target.files[0];
        if (file) {
            if (file.name.endsWith('.zip')) {
                try {
                    const zip = new JSZip();
                    const content = await zip.loadAsync(file);

                    if (content.files['metadata.csv']) {
                        const csvContent = await content.files['metadata.csv'].async('string');
                        const headers = csvContent.split('\n')[0].split(',').map((h: string) => h.trim());

                        if (this.entityType === BulkUploadEntityType.Category) {
                            const hasCategory = headers.some((h: string) => h.toLowerCase() === 'category');
                            const hasSubcategory = headers.some((h: string) => h.toLowerCase() === 'subcategory');

                            if (!hasCategory || !hasSubcategory) {
                                this.toastService.error('Invalid metadata.csv file. Category and Subcategory columns are required for Category upload.');
                                this.resetFileInput();
                                return;
                            }
                        } else if (this.entityType === BulkUploadEntityType.Department) {
                            const hasDepartment = headers.some((h: string) => h.toLowerCase() === 'department');
                            const hasCategory = headers.some((h: string) => h.toLowerCase() === 'category');
                            const hasSubcategory = headers.some((h: string) => h.toLowerCase() === 'subcategory');

                            if (!hasDepartment) {
                                this.toastService.error('Invalid metadata.csv file. Department column is required.');
                                this.resetFileInput();
                                return;
                            }

                            if (hasCategory || hasSubcategory) {
                                this.toastService.error('Invalid metadata.csv file. Category and Subcategory columns should not be present for Department upload.');
                                this.resetFileInput();
                                return;
                            }
                        }
                    } else {
                        this.toastService.error('metadata.csv not found in zip.');
                        this.resetFileInput();
                        return;
                    }

                    this.selectedFile = file;
                    this.uploadResult = null;
                } catch (e) {
                    console.error('Error reading zip file', e);
                    this.toastService.error('Failed to read ZIP file.');
                    this.resetFileInput();
                }
            } else {
                this.toastService.error('Please select a ZIP file.');
                this.resetFileInput();
            }
        }
    }

    resetFileInput() {
        this.selectedFile = null;
        if (this.fileInput) {
            this.fileInput.nativeElement.value = '';
        }
    }

    uploadIcons() {
        if (!this.selectedFile) return;

        this.loading = true;
        this.departmentService.bulkUploadIcons(this.selectedFile, this.companyId, this.entityType).subscribe({
            next: (res) => {
                this.uploadResult = res;
                this.loading = false;
                this.toastService.success('Bulk upload completed.');
            },
            error: (err) => {
                this.loading = false;
                this.toastService.error('Failed to upload icons.');
                console.error(err);
            }
        });
    }

    downloadTemplate() {
        let csvContent = "";
        if (this.entityType === BulkUploadEntityType.Department) {
            csvContent = "Department,IconFileName\nElectronics,electronics-icon.png\nClothing,clothing-icon.png";
        } else {
            csvContent = "Department,Category,Subcategory,IconFileName\nElectronics,Mobiles,Smartphones,iphone.png\nClothing,Mens,T-Shirts,tshirt.png";
        }
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${this.entityTypeName}_Icon_Template.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    }

    closeModal() {
        this.resetFileInput();
        this.uploadResult = null;
    }
}
